import { isFuture } from "date-fns";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MyTeamTnADayDataWithBreaksDTO } from "src/models/tna/tna-day-data-with-breaks/my-team-tna-day-data-with-breaks-dto";
import { TnADayDataWithBreaksRef } from "src/models/tna/tna-day-data-with-breaks/tna-day-data-with-breaks-ref";
import { LoadResponse } from "../../../../models/api/load";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import {
  getDayAndMonth,
  getDaysInMonth,
  getNormaleDateOrMonth,
  intlFormatDateShort,
  intlFormatDateWithHour,
} from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnABreaksData: (pageSize?: number) => Promise<LoadResponse<MyTeamTnADayDataWithBreaksDTO> | undefined>;
  allItems: () => MyTeamTnADayDataWithBreaksDTO[];
}

const ExportTnABreaksToExcel = ({ disabled, loadEmployeeGroup, loadTnABreaksData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: [t(`${tRP}.my_team_breaks`)],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`${t(`${tRP}.report_date`)} ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `${t(`${tRP}.legal_entities`)}: ${
              !legalEntityOption?.legalEntityIds?.length ? t(`${tRP}.all`) : selectedLegalEntity?.name ?? ""
            }`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `${t(`${tRP}.employee_group`)}: ${
              !employeeGroupOption?.employeeGroupIds?.length ? t(`${tRP}.all_groups`) : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `${t(`${tRP}.employee_status`)}: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees
                ? t(`${tRP}.only_current_employees`)
                : t(`${tRP}.all_employees`)
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `${t(`${tRP}.period`)}: ${year} ${t(
              monthsValues.find((currMonth) => currMonth.value === month)?.key ?? ""
            )}`,
          ],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);

    const dates: string[] = [];

    for (let i = 0; i < daysInMonth; i++) {
      dates.push(getDayAndMonth(new Date(year, month, i + 1)));
    }

    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      ...dates,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: MyTeamTnADayDataWithBreaksDTO[]) => {
      const daysInMonth = getDaysInMonth(year, month);

      return allItems.map((item) => {
        const dates: { [key: string]: { value: string } } = {};

        for (let i = 0; i < daysInMonth; i++) {
          const currDay = item?.daysData?.find((day: TnADayDataWithBreaksRef) => day?.tnADate?.day === i + 1);

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) !== -1;

          const absences = currDay?.absences
            .map(
              (absence) =>
                `${absence.absenceType?.code} ${
                  absence.isCancelled
                    ? `(${t(`${tRP}.cancelled`)})`
                    : absence.isApproved
                    ? `(${t(`${tRP}.approved`)})`
                    : absence.isRejected
                    ? `(${t(`${tRP}.rejected`)})`
                    : `(${t(`${tRP}.pending`)})`
                }`
            )
            .join(", ")
            .trim();

          // Calculate total non-automatic breaks
          let totalBreakMinutes = 0;
          const breaksInfo =
            currDay?.breaks && currDay.breaks.length > 0
              ? currDay.breaks
                  .map((timeBreak, index) => {
                    if (timeBreak.from && timeBreak.to) {
                      const fromDate = new Date(
                        timeBreak.from.year,
                        timeBreak.from.month - 1,
                        timeBreak.from.day,
                        timeBreak.from.hour,
                        timeBreak.from.minute,
                        timeBreak.from.second
                      );
                      const toDate = new Date(
                        timeBreak.to.year,
                        timeBreak.to.month - 1,
                        timeBreak.to.day,
                        timeBreak.to.hour,
                        timeBreak.to.minute,
                        timeBreak.to.second
                      );

                      const breakDurationMinutes = (toDate.getTime() - fromDate.getTime()) / (1000 * 60);
                      totalBreakMinutes += breakDurationMinutes;
                      return `${t(`${tRP}.break`)} ${index + 1}: ${breakDurationMinutes.toFixed(2)}min`;
                    }
                    return `${t(`${tRP}.break`)} ${index + 1}: 0min`;
                  })
                  .join("\n")
              : "";

          // Calculate total automatic breaks
          let totalAutomaticBreakMinutes = 0;
          const automaticBreaksInfo =
            currDay?.automaticBreaks && currDay.automaticBreaks.length > 0
              ? currDay.automaticBreaks
                  .map((timeBreak, index) => {
                    const breakDurationMinutes = timeBreak.substractedDuration || 0;
                    totalAutomaticBreakMinutes += breakDurationMinutes;
                    return `Ⓐ ${t(`${tRP}.break`)} ${index + 1}: ${breakDurationMinutes.toFixed(2)}min`;
                  })
                  .join("\n")
              : "";

          const presenceHours = currDay?.presence ? currDay.presence.toFixed(2) : "0";
          const totalBreaksSum = (totalBreakMinutes + totalAutomaticBreakMinutes).toFixed(2);

          let combinedInfo = "";

          if (!isFuture(CustomDate.ToDate(currDay?.tnADate))) {
            if (absences) {
              combinedInfo = `${absences} \n`;
            }
            if (currDay) {
              if (isWeekend && parseFloat(presenceHours) === 0 && parseFloat(totalBreaksSum) === 0) {
                combinedInfo += "";
              } else {
                combinedInfo += `${t(`${tRP}.worked_time`)}: ${presenceHours}h / ${t(
                  `${tRP}.breaks`
                )}: ${totalBreaksSum}min`;
                if (breaksInfo) {
                  combinedInfo += `\n${breaksInfo}`;
                }
                if (automaticBreaksInfo) {
                  combinedInfo += `\n${automaticBreaksInfo}`;
                }
              }
            }
          } else {
            if (absences) {
              combinedInfo = absences;
            }
          }

          dates[getDayAndMonth(new Date(year, month, i + 1))] = combinedInfo ? { value: combinedInfo } : { value: " " };
        }

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          ...dates,
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${
            !legalEntityOption?.legalEntityIds?.length
              ? t(`${tRP}.all_legal_entities`)
              : selectedLegalEntity?.name ?? ""
          } ${
            !employeeGroupOption?.employeeGroupIds?.length ? t(`${tRP}.all_groups`) : `${employeeGroup?.name ?? ""}`
          } ${t(`${tRP}.my_team_breaks`)}`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnABreaksData();

        if (firstResponse?.error) return;

        const allTnABreaksData = await loadTnABreaksData(firstResponse?.totalItems);

        if (!!allTnABreaksData?.error || !allTnABreaksData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnABreaksData?.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${
            !legalEntityOption?.legalEntityIds?.length
              ? t(`${tRP}.all_legal_entities`)
              : selectedLegalEntity?.name ?? ""
          } ${
            !employeeGroupOption?.employeeGroupIds?.length ? t(`${tRP}.all_groups`) : `${employeeGroup?.name ?? ""}`
          } ${t(`${tRP}.my_team_breaks`)}`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnABreaksData,
      allItems,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTnABreaksToExcel;
